import { ReactNode, useId, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { cn } from '@v2/shared/lib/utils';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { SideMenuGroup } from './menu';

type Props = {
  /** Labels the toggle the menu collapses behind on a phone. */
  label: string;
  /**
   * Names the landmark to a screen reader, when the toggle's wording is not what someone
   * scanning a page's regions wants to hear. Institutional's rail is "Customer
   * information" to a thumb and "About DiscountMags" to a landmark list, as it was before
   * this menu was shared.
   */
  ariaLabel?: string;
  groups: SideMenuGroup[];
  activePath?: string;
  /** Rendered under the last group — the institutional menu's way-on button. */
  footer?: ReactNode;
  className?: string;
};

/**
 * The side menu. Sticky beside the content from lg up; below that it collapses behind a
 * single toggle, because a dozen links above the page's own H1 is most of a phone screen.
 *
 * The links are always in the markup, open or closed — a crawler and an in-page search
 * both see the full list, and only the visibility class changes.
 *
 * `useId` rather than a fixed id: two menus on one page would otherwise both claim
 * `aria-controls="menu"`, and it is the one hook that is guaranteed to agree between the
 * server render and the hydration.
 */
export default function SideMenuNav({
  label,
  ariaLabel,
  groups,
  activePath,
  footer,
  className,
}: Props) {
  const { buildUrl } = useUrlGenerator();
  const [isOpen, setIsOpen] = useState(false);
  const panelId = useId();

  return (
    <nav aria-label={ariaLabel ?? label} className={cn('lg:sticky lg:top-6', className)}>
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => setIsOpen((open) => !open)}
        className={cn(
          'lg:hidden w-full flex items-center justify-between gap-3 cursor-pointer',
          'rounded-xl border border-solid border-slate-200 bg-white px-4 py-3',
          'text-left text-base font-semibold text-slate-800'
        )}
      >
        {label}
        <FontAwesomeIcon
          icon={faChevronDown}
          aria-hidden
          className={cn('transition-transform duration-200', isOpen && 'rotate-180')}
        />
      </button>

      <div
        id={panelId}
        className={cn(
          'rounded-xl border border-solid border-slate-200 bg-white py-2 shadow-sm',
          'mt-2 lg:mt-0 lg:block',
          !isOpen && 'hidden'
        )}
      >
        {groups.map((group) => (
          <div
            key={group.title}
            className="py-3 border-0 border-t border-solid border-slate-100 first:border-t-0"
          >
            <h2 className="px-5 pb-1 text-xs font-bold uppercase tracking-wider text-slate-500">
              {group.title}
            </h2>

            <ul className="list-none m-0 p-0">
              {group.items.map((item) => {
                const isActive = item.href === activePath;

                return (
                  <li key={item.href}>
                    <a
                      href={buildUrl(item.href)}
                      aria-current={isActive ? 'page' : undefined}
                      className={cn(
                        'flex items-center gap-2 px-5 py-2 text-sm no-underline',
                        'border-0 border-l-[3px] border-solid transition-colors',
                        isActive
                          ? 'border-primary bg-primary-50 font-semibold text-primary'
                          : 'border-transparent text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      )}
                    >
                      {item.label}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        {footer && <div className="px-4 pb-2 pt-3">{footer}</div>}
      </div>
    </nav>
  );
}
